type CategorizedResponse = {
  category?: unknown;
  errorCategory?: unknown;
  errorCode?: unknown;
  code?: unknown;
  relatedEntityName?: unknown;
  entityName?: unknown;
};

export type AdminOperation = 'company' | 'network' | 'companyGroup' | 'publisher' | 'title' | 'list' | 'product';

const categoryOf = (response: CategorizedResponse | undefined): string | undefined => {
  const category = response?.category ?? response?.errorCategory ?? response?.errorCode ?? response?.code;
  return typeof category === 'string' ? category : undefined;
};

// O backend pode fornecer nome de exibição para contexto. Nunca reutilizamos texto
// de erro arbitrário, nem valores que pareçam identificadores ou PII.
const safeDisplayName = (response: CategorizedResponse | undefined): string | undefined => {
  const name = response?.relatedEntityName ?? response?.entityName;
  if (typeof name !== 'string' || name.length === 0 || name.length > 120) return undefined;
  const containsNationalIdentifier = /(?:\d{3}\.?\d{3}\.?\d{3}-?\d{2}|\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2})/.test(name);
  if (containsNationalIdentifier || /\b\S+@\S+\.\S+\b|\b(?:token|bearer|id|uid)\s*[:=#-]?\s*[A-Za-z0-9_-]{3,}/i.test(name)) return undefined;
  return name;
};

const blockedMessages: Record<AdminOperation, Record<string, string>> = {
  company: {
    invalid_cnpj: 'Informe um CNPJ válido para a Empresa.',
    invalid_name: 'Informe um nome válido para a Empresa.',
    invalid_activity: 'Informe uma Atividade válida para a Empresa.',
    invalid_logo_url: 'Informe uma URL válida para o logo da Empresa.',
    invalid_network: 'Selecione uma Rede válida para a Empresa.',
    invalid_company_group: 'Selecione um Grupo válido para a Empresa.',
    group_not_found: 'O Grupo selecionado não foi encontrado. Atualize a listagem e tente novamente.',
    invalid_request: 'Revise os campos informados para a Empresa.',
    duplicate_cnpj: 'Já existe uma Empresa cadastrada com este CNPJ.',
    list_not_found: 'A Lista selecionada não foi encontrada.',
    has_entitlements: 'A Empresa não pode ser excluída porque possui vínculos ativos.',
    not_found: 'A Empresa não foi encontrada. Atualize a listagem e tente novamente.',
  },
  network: {
    'failed-precondition': 'A Rede não pode ser excluída neste momento porque possui muitos vínculos.',
    not_found: 'A Rede não foi encontrada. Atualize a listagem e tente novamente.',
  },
  companyGroup: {
    'functions/already-exists': 'Já existe um Grupo com este nome.',
    'functions/failed-precondition': 'O Grupo não pode ser excluído porque possui mais de 250 Empresas vinculadas. Desvincule algumas Empresas e tente novamente.',
    'functions/not-found': 'O Grupo ou a Empresa não foi encontrado. Atualize a listagem e tente novamente.',
    'functions/aborted': 'O vínculo foi alterado por outra sessão. A listagem foi recarregada.',
    'functions/invalid-argument': 'Revise os campos informados para o Grupo.',
  },
  publisher: {
    invalid_name: 'Informe um nome válido para a Editora.',
    invalid_request: 'Revise os campos informados para a Editora.',
    has_linked_titles: 'A Editora não pode ser excluída porque possui títulos vinculados.',
    not_found: 'A Editora não foi encontrada. Atualize a listagem e tente novamente.',
  },
  title: {
    has_entitlements: 'O Título não pode ser excluído porque possui vínculos ativos.',
    has_pending_entitlements: 'O Título não pode ser excluído porque possui acessos pendentes.',
    has_sessions: 'O Título não pode ser excluído porque possui sessões vinculadas.',
    used_by_list: 'O Título não pode ser excluído porque pertence a uma Lista.',
    not_found: 'O Título não foi encontrado. Atualize a listagem e tente novamente.',
  },
  list: {
    invalid_name: 'Informe um nome válido para a Lista.',
    invalid_description: 'Informe uma descrição válida para a Lista.',
    invalid_items: 'Revise os títulos informados para a Lista.',
    invalid_product: 'Selecione um Ativo válido para a Lista.',
    invalid_request: 'Revise os campos informados para a Lista.',
    used_by_company: 'A Lista não pode ser excluída porque está vinculada a uma Empresa.',
    not_found: 'A Lista não foi encontrada. Atualize a listagem e tente novamente.',
  },
  product: {
    invalid_title: 'Informe um título válido para o Ativo.',
    invalid_type: 'Selecione um tipo válido para o Ativo.',
    invalid_request: 'Revise os campos informados para o Ativo.',
    used_by_combo: 'O Ativo não pode ser excluído porque é usado por um Combo.',
    used_by_list: 'O Ativo não pode ser excluído porque é usado por uma Lista.',
    not_found: 'O Ativo não foi encontrado. Atualize a listagem e tente novamente.',
  },
};

export const hasFunctionsErrorCode = (error: unknown, code: string): boolean => (
  (error as { code?: unknown } | null)?.code === `functions/${code}`
);

export const companyGroupErrorMessage = (error: unknown): string => (
  operationErrorMessage('companyGroup', { code: (error as { code?: unknown } | null)?.code })
);

export function operationErrorMessage(operation: AdminOperation, response?: CategorizedResponse): string {
  const category = categoryOf(response);
  const message = category ? blockedMessages[operation][category] : undefined;
  if (!message) return `Não foi possível concluir a operação em ${operation === 'company' ? 'Empresa' : operation === 'network' ? 'Rede' : operation === 'companyGroup' ? 'Grupo' : operation === 'publisher' ? 'Editora' : operation === 'title' ? 'Título' : operation === 'list' ? 'Lista' : 'Ativo'}.`;

  const name = safeDisplayName(response);
  return name && category === 'used_by_company'
    ? `${message} Empresa: ${name}.`
    : message;
}
